import { API_URL } from '../components/common/Config';

const GLOBAL_ROLES_KEY = '__ebussoUserRolesCache';
const GLOBAL_PERMISSIONS_KEY = '__ebussoUserRolesPermissionsCache';

let fallbackRolesStore = null;
let fallbackPermissionsStore = null;

function getPrefix() {
  return localStorage.getItem('accessToken') || 'default';
}

function rolesSessionKey(userId) {
  return `ebusso_user_roles_${getPrefix()}_${userId}`;
}

function permissionsSessionKey(userId, roleId = null) {
  return `ebusso_user_roles_perm_${getPrefix()}_${userId}_${roleId || 'all'}`;
}

function hydratedSessionKey(userId) {
  return `ebusso_roles_hydrated_${getPrefix()}_${userId}`;
}

function readJsonSession(key) {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeJsonSession(key, value) {
  if (typeof window === 'undefined' || value === undefined) {
    return;
  }
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function removeSessionKey(key) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function buildCacheKey(userId, bearerToken) {
  return `${bearerToken || ''}:${userId || ''}`;
}

function buildPermissionsCacheKey(userId, bearerToken, roleId = null) {
  return `${bearerToken || ''}:${userId || ''}:${roleId || 'all'}`;
}

function getRolesStore() {
  if (typeof window !== 'undefined') {
    if (!window[GLOBAL_ROLES_KEY]) {
      window[GLOBAL_ROLES_KEY] = {
        cacheKey: null,
        cachedResponse: undefined,
        inflightPromise: null,
        inflightKey: null,
      };
    }
    return window[GLOBAL_ROLES_KEY];
  }
  if (!fallbackRolesStore) {
    fallbackRolesStore = {
      cacheKey: null,
      cachedResponse: undefined,
      inflightPromise: null,
      inflightKey: null,
    };
  }
  return fallbackRolesStore;
}

function getPermissionsStore() {
  if (typeof window !== 'undefined') {
    if (!window[GLOBAL_PERMISSIONS_KEY]) {
      window[GLOBAL_PERMISSIONS_KEY] = {
        entries: {},
        inflight: {},
      };
    }
    return window[GLOBAL_PERMISSIONS_KEY];
  }
  if (!fallbackPermissionsStore) {
    fallbackPermissionsStore = {
      entries: {},
      inflight: {},
    };
  }
  return fallbackPermissionsStore;
}

export function isRolesSessionHydrated(userId) {
  if (!userId || typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(hydratedSessionKey(userId)) === '1';
}

export function markRolesSessionHydrated(userId) {
  if (!userId || typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(hydratedSessionKey(userId), '1');
  } catch {
    // ignore
  }
}

export function clearUserRolesCache() {
  const userId = localStorage.getItem('userId');
  const store = getRolesStore();
  store.cacheKey = null;
  store.cachedResponse = undefined;
  store.inflightPromise = null;
  store.inflightKey = null;

  const permStore = getPermissionsStore();
  permStore.entries = {};
  permStore.inflight = {};

  if (typeof window !== 'undefined') {
    const prefix = getPrefix();
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.startsWith(`ebusso_user_roles_${prefix}_`) ||
          key.startsWith(`ebusso_user_roles_perm_${prefix}_`) ||
          key.startsWith(`ebusso_roles_hydrated_${prefix}_`))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => removeSessionKey(key));
  } else if (userId) {
    removeSessionKey(rolesSessionKey(userId));
    removeSessionKey(hydratedSessionKey(userId));
    removeSessionKey(permissionsSessionKey(userId, null));
  }
}

export function getCachedUserRolesResponse(userId, bearerToken) {
  const store = getRolesStore();
  const key = buildCacheKey(userId, bearerToken);
  if (store.cacheKey === key && store.cachedResponse !== undefined) {
    return store.cachedResponse;
  }
  const fromSession = readJsonSession(rolesSessionKey(userId));
  if (fromSession !== undefined) {
    store.cacheKey = key;
    store.cachedResponse = fromSession;
    return fromSession;
  }
  return null;
}

export function prefetchUserRolesForPermission() {
  const userId = localStorage.getItem('userId');
  const accessToken = localStorage.getItem('accessToken');
  if (!userId || !accessToken) {
    return Promise.resolve(null);
  }
  return fetchUserRolesForPermission(userId, accessToken);
}

export async function fetchUserRolesForPermission(userId, bearerToken) {
  if (!userId || !bearerToken) {
    return null;
  }

  const cacheKey = buildCacheKey(userId, bearerToken);
  const store = getRolesStore();

  if (store.cacheKey === cacheKey && store.cachedResponse !== undefined) {
    return store.cachedResponse;
  }

  const fromSession = readJsonSession(rolesSessionKey(userId));
  if (fromSession !== undefined) {
    store.cacheKey = cacheKey;
    store.cachedResponse = fromSession;
    return fromSession;
  }

  if (store.inflightPromise && store.inflightKey === cacheKey) {
    return store.inflightPromise;
  }

  store.inflightKey = cacheKey;
  store.inflightPromise = (async () => {
    try {
      const formData = new FormData();
      formData.append('userId', String(userId));

      const response = await fetch(`${API_URL}/get_users_role_for_permission`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      const result = {
        ok: response.ok,
        status: response.status,
        data,
      };

      store.cacheKey = cacheKey;
      store.cachedResponse = result;
      writeJsonSession(rolesSessionKey(userId), result);
      return result;
    } catch {
      return null;
    } finally {
      store.inflightPromise = null;
      store.inflightKey = null;
    }
  })();

  return store.inflightPromise;
}

export function getCachedUserRolesWithPermissions(userId, bearerToken, roleId = null) {
  const store = getPermissionsStore();
  const key = buildPermissionsCacheKey(userId, bearerToken, roleId);
  if (store.entries[key] !== undefined) {
    return store.entries[key];
  }
  const fromSession = readJsonSession(permissionsSessionKey(userId, roleId));
  if (fromSession !== undefined) {
    store.entries[key] = fromSession;
    return fromSession;
  }
  return null;
}

export async function fetchUserRolesWithPermissionsCached(
  bearerToken,
  { userId, roleId = null, force = false } = {}
) {
  if (!userId || !bearerToken) {
    return null;
  }

  const key = buildPermissionsCacheKey(userId, bearerToken, roleId);
  const store = getPermissionsStore();

  if (!force) {
    if (store.entries[key] !== undefined) {
      return store.entries[key];
    }
    const fromSession = readJsonSession(permissionsSessionKey(userId, roleId));
    if (fromSession !== undefined) {
      store.entries[key] = fromSession;
      return fromSession;
    }
  }

  if (store.inflight[key]) {
    return store.inflight[key];
  }

  store.inflight[key] = (async () => {
    try {
      const { postUserRolesWithPermissions } = await import('./rolesApi');
      const result = await postUserRolesWithPermissions(bearerToken, { userId, roleId });
      store.entries[key] = result;
      writeJsonSession(permissionsSessionKey(userId, roleId), result);
      return result;
    } catch {
      return null;
    } finally {
      delete store.inflight[key];
    }
  })();

  return store.inflight[key];
}

export function prefetchUserRolesWithPermissions() {
  const userId = localStorage.getItem('userId');
  const accessToken = localStorage.getItem('accessToken');
  if (!userId || !accessToken) {
    return Promise.resolve(null);
  }
  return fetchUserRolesWithPermissionsCached(accessToken, { userId });
}
